import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { moderationApi } from './moderationApi'
import type { ModerationState } from './types'

export const MODERATION_POLL_MS = 3000
/** Повтор, пока первая загрузка состояния падает (данных нет вовсе). */
export const MODERATION_ERROR_RETRY_MS = 10_000
/** Опрос живого курса/статьи вне проверки: черновик могли завести ВНЕ студии (MCP,
 *  вторая вкладка), и иначе студия узнала бы о нём только с перезагрузкой. */
export const DRAFT_POLL_MS = 30_000

/**
 * Чтение кеша БЕЗ подписки: потребителей десятки (лок в каждой панели), а
 * запрос ведёт один `ModerationPoller`. «Не знаем» — `state === undefined`:
 * лок при этом не ставится (окно до первого ответа принято, страховка — `409`).
 */
export const useCourseModeration = (
  courseId: number,
): { state: ModerationState | undefined; isError: boolean; refetch: () => void } => {
  const dispatch = useAppDispatch()
  const select = useMemo(
    () => moderationApi.endpoints.getCourseModeration.select(courseId),
    [courseId],
  )
  const result = useAppSelector(select)
  const refetch = useCallback(() => {
    void dispatch(
      moderationApi.endpoints.getCourseModeration.initiate(courseId, {
        forceRefetch: true,
        subscribe: false,
      }),
    )
  }, [courseId, dispatch])
  return { state: result.data, isError: result.isError, refetch }
}

export const useIsCourseUnderReview = (courseId: number): boolean =>
  useCourseModeration(courseId).state?.review?.status === 'pending'

/**
 * «Контент курса заморожен» — то же, что `useIsCourseUnderReview`, но под своим
 * именем для путей, которым прогон перевода НЕ мешает, а проверка мешает
 * (настройки, уроки). Отдельное имя — чтобы не расширять смысл
 * `useIsCourseTextLocked`. Сквозные поля (цены, режим продажи, пробные уроки,
 * категории, `isHiddenOn*`) НЕ замораживаются: контракт §3a пишет их без
 * проверки и во время неё.
 */
export const useIsCourseFrozen = (courseId: number): boolean => useIsCourseUnderReview(courseId)
